import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Marker, Polyline, Tooltip } from "react-leaflet";
import Leaflet from "leaflet";
import { convertPositions } from '../../../utils/helpers';
import guid from './../../../utils/guid';
import moment from 'moment-timezone';
import t from "../../../languages/pl";
import { displayDateTimeFormat } from '../../../utils/staticData';
import finishRoutePin from "../../../images/mapPins/finishRoutePin.png";
import startRoutePin from "../../../images/mapPins/startRoutePin.png";
import { getAddress } from '../../../requests/mapRequests';

const startIcon = new Leaflet.Icon({
    iconUrl: startRoutePin,
    iconSize: [50, 50],
    iconAnchor: [25, 50]
});

const finishIcon = new Leaflet.Icon({
    iconUrl: finishRoutePin,
    iconSize: [50, 50],
    iconAnchor: [25, 50]
});

const standardSpeedColor = {
    color: "#a3469e",
    weight: 5,
};

const speedExceededColor = {
    color: "#f4853f",
    weight: 5,
};

const speedExceededAbove30Color = {
    color: "#ff0000",
    weight: 5,
};

const speedExceededAbove50Color = {
    color: "#000000",
    weight: 5,
};

const selectStyle = (speedLimit, speed) => {
    if (speedLimit === -1 || speed < speedLimit)
        return standardSpeedColor;

    if (speed >= speedLimit + 10 && speed <= speedLimit + 30)
        return speedExceededColor;

    if (speed > speedLimit + 30 && speed <= speedLimit + 50)
        return speedExceededAbove30Color;

    if (speed > speedLimit + 50)
        return speedExceededAbove50Color;

    return standardSpeedColor;
};

const SingleRoute = props => {
    const { route } = props;
    const [startAddressSearching, setStartAddressSearching] = useState(false);
    const [endAddressSearching, setEndAddressSearching] = useState(false);

    const revealStartAddress = (id, point) => {
        if (point.adres === null && !startAddressSearching) {
            getAddress(point, (response) => props.updateStartAddress(id, response.data.adres));
            setStartAddressSearching(true);
        }
    };

    const revealEndAddress = (id, point) => {
        if (point.adres === null && !endAddressSearching) {
            getAddress(point, (response) => props.updateEndAddress(id, response.data.adres));
            setEndAddressSearching(true);
        }
    };

    const printPolylines = () => {
        return route.trasa.map((routePart, index) => {
            if (route.trasa && index < route.trasa.length - 1) {
                return (
                    <Polyline
                        key={guid()}
                        positions={convertPositions([routePart, route.trasa[index + 1]])}
                        {...selectStyle(routePart.predkosc_dopuszczalna, routePart.predkosc)}
                    >
                        <Tooltip
                            direction="top"
                            className="toyotaFont"
                            sticky
                        >
                            {t.route}
                            <br />
                            {moment.unix(route.data_od).format(displayDateTimeFormat)} : {moment.unix(route.data_do).format(displayDateTimeFormat)}
                            <br />
                             {t.speedMax} {route.predkosc_max}  km/h.
                            <br />
                            {t.speedAvr} {route.predkosc_sr}  km/h.
                            <br />
                            {routePart.predkosc_dopuszczalna !== -1 && routePart.predkosc >= routePart.predkosc_dopuszczalna + 10
                              ?
                              <Fragment>
                                  {t.speedLimit} {routePart.predkosc_dopuszczalna}  km/h.
                                  <br />
                                  {t.speedWhenCrossing}  {routePart.predkosc}  km/h.
                                  <br />
                                  {t.speedExceededBy} {routePart.predkosc - routePart.predkosc_dopuszczalna} km/h
                              </Fragment>
                              : null}
                        </Tooltip>
                    </Polyline>
                );
            }
        });
    };

    return (
        <React.Fragment>
            {printPolylines()}
            {route.trasa[0] && <Marker
                position={convertPositions(route.trasa[0])}
                icon={startIcon}
            >
                <Tooltip
                    direction="top"
                    className="toyotaFont"
                    offset={new Leaflet.Point(0, -50)}
                    onOpen={() => revealStartAddress(route.id, route.trasa[0])}
                >
                    {t.routeBegin}
                    <br />
                    {t.date}: {moment.unix(route.data_od).format(displayDateTimeFormat)}
                    <br />
                    {t.meterReading}: {route.trasa[0].dystans_przed} km
                            <br />
                    {t.fuelLevel}:&nbsp;
                            {route.trasa[0].stan_paliwa_przed} l ({Math.round(route.trasa[0].stan_paliwa_procent_przed * 100)}%)
                            <br />
                    {route.trasa[0].adres ? route.trasa[0].adres : "Szukam adresu..."}
                </Tooltip>
            </Marker>}
            {route.trasa[route.trasa.length - 1] && <Marker
                position={convertPositions(route.trasa[route.trasa.length - 1])}
                icon={finishIcon}
            >
                <Tooltip
                    direction="top"
                    className="toyotaFont"
                    offset={new Leaflet.Point(0, -50)}
                    onOpen={() => revealEndAddress(route.id, route.trasa[route.trasa.length - 1])}
                >
                    {t.routeFinish}
                    <br />
                    {t.date}: {moment.unix(route.data_do).format(displayDateTimeFormat)}
                    <br />
                    {t.meterReading}: {route.trasa[route.trasa.length - 1].dystans_po} km
                            <br />
                    {t.fuelLevel}:&nbsp;
                            {route.trasa[route.trasa.length - 1].stan_paliwa_po} l ({Math.round(route.trasa[route.trasa.length - 1].stan_paliwa_procent_po * 100)}%)
                            <br />
                    {route.trasa[route.trasa.length - 1].adres ? route.trasa[route.trasa.length - 1].adres : "Szukam adresu..."}
                </Tooltip>
            </Marker>}
        </React.Fragment>
    );
};

SingleRoute.propTypes = {
    route: PropTypes.object,
    updateStartAddress: PropTypes.func,
    updateEndAddress: PropTypes.func
};

export default SingleRoute;
